class FormElements {
  constructor(container) {
    this.container = container;
    this.config = {
      rangeDistance: 1,
    };

    this.elements = this.cacheElements();
    this.init();
  }

  /**
   * Cache DOM elements using BEM selectors
   */
  cacheElements() {
    return {
      form: this.container.querySelector('.brand-venues__form'),
      rangeElements: this.container.querySelectorAll('.brand-venues__range'),
      listElements: this.container.querySelectorAll('.brand-venues__destinations'),
      chips: this.container.querySelectorAll('.brand-venues__chip'),
      popup: this.container.querySelector('#brand-venues-popup--filter'),
      expanderFieldset: this.container.querySelector('.brand-venues__fieldset--expander'),
      fieldsetsClearer: this.container.querySelectorAll('.brand-venues__filter-clearer'),
    };
  }

  init() {
    this.elements.popup.style.display = 'block';
    this.initChildHeights();
    this.initExpanderFieldset();

    if (this.elements.rangeElements.length > 0) {
      this.elements.rangeElements.forEach((rangeElement) => {
        const rangeInputs = rangeElement.querySelectorAll('.brand-venues__range-input');
        // Range input events
        rangeInputs.forEach((input) => {
          input.addEventListener('input', (e) => {
            this.handleRangeInput(e.target, rangeElement);
          });
        });

        FormElements.updateRangeMarker(rangeElement);
      });

      if (this.elements.form) {
        this.elements.form.addEventListener('reset', () => {
          this.elements.rangeElements.forEach((rangeElement) => {
            setTimeout(() => {
              FormElements.updateRangeMarker(rangeElement);
            }, 0);
          });
        });
      }

      if (this.elements.fieldsetsClearer) {
        this.elements.fieldsetsClearer.forEach((clearer) => {
          clearer.addEventListener('click', (event) => {
            event.preventDefault();

            const fieldset = clearer.closest('fieldset');
            const inputs = fieldset.querySelectorAll('input');

            if (inputs) {
              inputs.forEach((input) => {
                if (input.getAttribute('type') === 'checkbox') {
                  // eslint-disable-next-line no-param-reassign
                  input.checked = false;
                }
              });
            }

            const destinations = fieldset.querySelector('.brand-venues__destinations');
            if (destinations) {
              FormElements.syncParentHighlightStates(destinations);
            }
          });
        });
      }

      this.elements?.chips.forEach((chip) => {
        chip.addEventListener('keydown', (e) => {
          if (e.keyCode === 32) {
            e.preventDefault();
            e.stopPropagation();
            e.target.click();
          }
        });
      });
    }

    if (this.elements.listElements.length > 0) {
      this.elements.listElements.forEach((listElement) => {
        const parentArrows = listElement.querySelectorAll('.brand-venues__destination-arrow');

        parentArrows.forEach((parentArrow) => {
          if (!parentArrow.hasAttribute('tabindex')) {
            parentArrow.setAttribute('tabindex', '0');
          }
          parentArrow.setAttribute('role', 'button');

          const parentRow = parentArrow.closest('.brand-venues__destination-row--parent');
          const relatedChildList = parentRow?.nextElementSibling;
          if (relatedChildList && relatedChildList.classList.contains('brand-venues__destination-child')) {
            if (!relatedChildList.id) {
              // eslint-disable-next-line no-param-reassign
              relatedChildList.id = `brand-venues-child-${Math.random().toString(36).slice(2)}`;
            }
            parentArrow.setAttribute('aria-controls', relatedChildList.id);
            parentArrow.setAttribute('aria-expanded', relatedChildList.classList.contains('brand-venues__destination-child--open') ? 'true' : 'false');
          }

          parentArrow.addEventListener('click', (event) => {
            FormElements.initParentCheckboxExpander(event);
          });

          parentArrow.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.keyCode === 13 || event.keyCode === 32) {
              event.preventDefault();
              event.stopPropagation();
              FormElements.initParentCheckboxExpander(event);
            }
          });
        });

        // Add parent checkbox event listeners
        const parentCheckboxes = listElement.querySelectorAll('.brand-venues__destination-row--parent input[type="checkbox"]');
        parentCheckboxes.forEach((parentCheckbox) => {
          parentCheckbox.addEventListener('change', (e) => {
            FormElements.handleParentCheckboxChange(e);
          });
        });

        // Add child checkbox event listeners
        const childCheckboxes = listElement.querySelectorAll('.brand-venues__destination-row--child input[type="checkbox"]');
        childCheckboxes.forEach((childCheckbox) => {
          childCheckbox.addEventListener('change', (e) => {
            FormElements.handleChildCheckboxChange(e);
          });
        });

        const destinationLabels = listElement.querySelectorAll('.brand-venues__destination-label');
        destinationLabels.forEach((label) => {
          if (!label.hasAttribute('tabindex')) {
            label.setAttribute('tabindex', '0');
          }

          const input = label.querySelector('input[type="checkbox"]');
          if (input) {
            input.addEventListener('keydown', (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                input.click();
              }
            });
          }

          label.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.keyCode === 13 || e.keyCode === 32) {
              e.preventDefault();
              e.stopPropagation();
              const inputEl = label.querySelector('input[type="checkbox"]');
              if (inputEl) {
                inputEl.click();
              }
            }
          });
        });

        FormElements.syncParentHighlightStates(listElement);
      });
    }

    if (this.elements.form) {
      this.elements.form.addEventListener('reset', () => {
        requestAnimationFrame(() => {
          this.elements.listElements.forEach((listElement) => {
            FormElements.syncParentHighlightStates(listElement);
          });
        });
      });
    }

    this.elements.popup.style.display = 'none';
  }

  /**
   * Set height for child wrapper
   */
  initChildHeights() {
    const childElements = this.container.querySelectorAll('.brand-venues__destination-child');

    childElements.forEach((child) => {
      FormElements.recalculateOpenHeight(child);
      child.setAttribute('hidden', true);
    });
  }

  /**
   * Handle range input changes
   * @param target
   * @param rangeElement
   */
  handleRangeInput(target, rangeElement) {
    const minInput = rangeElement.querySelector('.brand-venues__range-input--min');
    const maxInput = rangeElement.querySelector('.brand-venues__range-input--max');
    const minValue = parseInt(minInput.value, 10);
    const maxValue = parseInt(maxInput.value, 10);

    // Prevent min from being greater than max
    if (maxValue - minValue < this.config.rangeDistance) {
      if (target.classList.contains('brand-venues__range-input--min')) {
        minInput.value = maxValue - this.config.rangeDistance;
      } else {
        maxInput.value = minValue + this.config.rangeDistance;
      }
    }

    FormElements.updateRangeMarker(rangeElement);
  }

  /**
   * Update the visual range display
   */
  static updateRangeMarker(rangeElement) {
    const rangeBetween = rangeElement.querySelector('.brand-venues__range-selected');
    const minRangeInput = rangeElement.querySelector('.brand-venues__range-input--min');
    const maxRangeInput = rangeElement.querySelector('.brand-venues__range-input--max');
    const rangeOutputValueMin = rangeElement.querySelector('.brand-venues__range-output--min .brand-venues__range-output-value');
    const rangeOutputValueMax = rangeElement.querySelector('.brand-venues__range-output--max .brand-venues__range-output-value');
    const rangeOutputMin = rangeElement.querySelector('.brand-venues__range-output--min');
    const rangeOutputMax = rangeElement.querySelector('.brand-venues__range-output--max');
    const minValue = parseInt(minRangeInput.value, 10);
    const maxValue = parseInt(maxRangeInput.value, 10);
    const maxRange = parseInt(minRangeInput.max, 10);

    const leftPercent = (minValue / maxRange) * 100;
    const rightPercent = 100 - (maxValue / maxRange) * 100;

    rangeBetween.style.left = `${leftPercent}%`;
    rangeBetween.style.right = `${rightPercent}%`;

    if (rangeOutputValueMin) {
      rangeOutputValueMin.textContent = minValue;
    }
    if (rangeOutputValueMax) {
      rangeOutputValueMax.textContent = maxValue;
    }

    if (rangeOutputMin) {
      rangeOutputMin.style.left = `${leftPercent}%`;
    }

    if (rangeOutputMax) {
      rangeOutputMax.style.right = `${rightPercent}%`;
    }
  }

  /**
   * Handle parent checkbox change
   * @param {Event} event - Checkbox change event
   */
  static handleParentCheckboxChange(event) {
    const parentCheckbox = event.target;
    const parentRow = parentCheckbox.closest('.brand-venues__destination-row--parent');
    const childContainer = parentRow.nextElementSibling;

    if (childContainer && childContainer.classList.contains('brand-venues__destination-child')) {
      const childCheckboxes = childContainer.querySelectorAll('.brand-venues__destination-row--child input[type="checkbox"]');
      const hasCheckedChildren = childContainer.querySelector('.brand-venues__destination-row--child input[type="checkbox"]:checked');

      if (hasCheckedChildren && parentCheckbox.checked) {
        childCheckboxes.forEach((childCheckbox) => {
          // eslint-disable-next-line no-param-reassign
          childCheckbox.checked = false;
        });

        parentCheckbox.checked = false;
        parentCheckbox.indeterminate = false;
        FormElements.setParentActiveState(parentRow, false);
        return;
      }

      // Update all child checkboxes to match parent state
      childCheckboxes.forEach((childCheckbox) => {
        // eslint-disable-next-line no-param-reassign
        childCheckbox.checked = parentCheckbox.checked;
      });

      FormElements.setParentActiveState(parentRow, parentCheckbox.checked);
    }
  }

  /**
   * Handle child checkbox change
   * @param {Event} event - Checkbox change event
   */
  static handleChildCheckboxChange(event) {
    const childCheckbox = event.target;
    const childContainer = childCheckbox.closest('.brand-venues__destination-child');
    const parentRow = childContainer.previousElementSibling;
    const parentCheckbox = parentRow.querySelector('.brand-venues__destination-row--parent input[type="checkbox"]');

    if (parentCheckbox) {
      const allChildCheckboxes = childContainer.querySelectorAll('.brand-venues__destination-row--child input[type="checkbox"]');
      const checkedChildCheckboxes = childContainer.querySelectorAll('.brand-venues__destination-row--child input[type="checkbox"]:checked');

      // Update parent checkbox state based on child checkboxes
      if (checkedChildCheckboxes.length === 0) {
        // No children checked - uncheck parent
        parentCheckbox.checked = false;
        parentCheckbox.indeterminate = false;
      } else if (checkedChildCheckboxes.length === allChildCheckboxes.length) {
        // All children checked - check parent
        parentCheckbox.checked = true;
        parentCheckbox.indeterminate = false;
      } else {
        // Some children checked - set parent to indeterminate
        parentCheckbox.checked = false;
        parentCheckbox.indeterminate = true;
      }

      const shouldHighlight = checkedChildCheckboxes.length > 0 || parentCheckbox.checked;
      FormElements.setParentActiveState(parentRow, shouldHighlight);
    }
  }

  static initParentCheckboxExpander(event) {
    const { target } = event;
    const relatedChildList = target.closest('.brand-venues__destination-row--parent').nextElementSibling;
    const parentRow = target.closest('.brand-venues__destination-row--parent');
    const arrow = parentRow ? parentRow.querySelector('.brand-venues__destination-arrow') : null;

    if (relatedChildList.classList.contains('brand-venues__destination-child')) {
      relatedChildList.classList.toggle('brand-venues__destination-child--open');

      if (relatedChildList.classList.contains('brand-venues__destination-child--open')) {
        FormElements.setAriaExpander(relatedChildList, true);
        if (arrow) {
          arrow.setAttribute('aria-expanded', 'true');
        }

        const childHeight = FormElements.recalculateOpenHeight(relatedChildList);

        // Update parent height first, then animate child
        FormElements.updateParentExpanderHeight(relatedChildList);

        requestAnimationFrame(() => {
          relatedChildList.style.maxHeight = `${childHeight}px`;
        });
      } else {
        relatedChildList.style.maxHeight = '0px';

        // Update parent height after child animation completes
        setTimeout(() => {
          FormElements.setAriaExpander(relatedChildList, false);
          FormElements.updateParentExpanderHeight(relatedChildList);
          if (arrow) {
            arrow.setAttribute('aria-expanded', 'false');
          }
        }, 300);
      }
    }
  }

  static updateParentExpanderHeight(childElement) {
    const legendExpander = childElement.closest('.brand-venues__fieldset-body');

    if (legendExpander && legendExpander.classList.contains('brand-venues__fieldset-body--open')) {
      const baseHeight = parseInt(legendExpander.getAttribute('data-open-size'), 10);
      const resolvedBaseHeight = Number.isNaN(baseHeight)
        ? FormElements.recalculateOpenHeight(legendExpander) : baseHeight;

      // Calculate total height of all open children
      const openChildren = legendExpander.querySelectorAll('.brand-venues__destination-child--open');
      let totalChildHeight = 0;

      openChildren.forEach((child) => {
        const childSize = parseInt(child.getAttribute('data-open-size'), 10);
        totalChildHeight += Number.isNaN(childSize)
          ? FormElements.recalculateOpenHeight(child) : childSize;
      });

      legendExpander.style.maxHeight = `${resolvedBaseHeight + totalChildHeight}px`;
    }
  }

  static setAriaExpander(target, isOpen) {
    if (isOpen) {
      target.setAttribute('aria-hidden', 'false');
      target.removeAttribute('hidden');
    } else {
      target.setAttribute('aria-hidden', 'true');
      target.setAttribute('hidden', true);
    }
  }

  /**
   * Initialize collapsible fieldsets
   */
  initExpanderFieldset() {
    const { expanderFieldset } = this.elements;
    const fieldsetBody = expanderFieldset.querySelector('.brand-venues__fieldset-body');
    const legend = expanderFieldset.querySelector('.brand-venues__legend--expander');

    if (fieldsetBody && legend) {
      // Calculate and store height
      const originalHeight = fieldsetBody.style.height;
      const originalOverflow = fieldsetBody.style.overflow;

      fieldsetBody.removeAttribute('hidden');
      // eslint-disable-next-line no-param-reassign
      fieldsetBody.style.height = 'auto';
      // eslint-disable-next-line no-param-reassign
      fieldsetBody.style.overflow = 'visible';

      const height = FormElements.recalculateOpenHeight(fieldsetBody);

      // eslint-disable-next-line no-param-reassign
      fieldsetBody.style.height = originalHeight;
      // eslint-disable-next-line no-param-reassign
      fieldsetBody.style.overflow = originalOverflow;

      // Start open
      fieldsetBody.classList.add('brand-venues__fieldset-body--open');
      // eslint-disable-next-line no-param-reassign
      fieldsetBody.style.maxHeight = `${height}px`;

      legend.setAttribute('role', 'button');
      if (!legend.hasAttribute('tabindex')) {
        legend.setAttribute('tabindex', '0');
      }
      if (!fieldsetBody.id) {
        // eslint-disable-next-line no-param-reassign
        fieldsetBody.id = `brand-venues-fieldset-${Math.random().toString(36).slice(2)}`;
      }
      legend.setAttribute('aria-controls', fieldsetBody.id);
      legend.setAttribute('aria-expanded', 'true');

      legend.addEventListener('click', (e) => {
        FormElements.toggleFieldsetBody(e);
      });

      legend.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.keyCode === 13 || e.keyCode === 32) {
          e.preventDefault();
          e.stopPropagation();
          FormElements.toggleFieldsetBody(e);
        }
      });
    }
  }

  /**
   * Toggle fieldset body (same logic as initParentCheckboxExpander)
   */
  static toggleFieldsetBody(event) {
    const { target } = event;
    const legend = target.closest('.brand-venues__legend--expander');
    const fieldset = legend.closest('.brand-venues__fieldset--expander');
    const fieldsetBody = fieldset.querySelector('.brand-venues__fieldset-body');

    // Toggle the open class
    fieldsetBody.classList.toggle('brand-venues__fieldset-body--open');

    setTimeout(() => {
      if (fieldsetBody.classList.contains('brand-venues__fieldset-body--open')) {
        FormElements.setAriaExpander(fieldsetBody, true);
        legend.setAttribute('aria-expanded', 'true');

        setTimeout(() => {
          // eslint-disable-next-line no-param-reassign
          fieldsetBody.style.maxHeight = `${FormElements.recalculateOpenHeight(fieldsetBody)}px`;
        }, 100);
      } else {
        // eslint-disable-next-line no-param-reassign
        fieldsetBody.style.maxHeight = '0px';

        setTimeout(() => {
          // Close all child expanders first
          const openChildren = fieldsetBody.querySelectorAll('.brand-venues__destination-child--open');
          openChildren.forEach((child) => {
            // eslint-disable-next-line no-param-reassign
            child.style.maxHeight = '0px';
            child.classList.remove('brand-venues__destination-child--open');
            FormElements.setAriaExpander(child, false);
          });

          FormElements.setAriaExpander(fieldsetBody, false);
          legend.setAttribute('aria-expanded', 'false');
        }, 300);
      }
    }, 0);
  }

  static recalculateOpenHeight(element) {
    if (!element) {
      return 0;
    }

    const wasHidden = element.hasAttribute('hidden');
    const originalHeight = element.style.height;
    const originalOverflow = element.style.overflow;

    if (wasHidden) {
      element.removeAttribute('hidden');
    }

    // eslint-disable-next-line no-param-reassign
    element.style.height = 'auto';
    // eslint-disable-next-line no-param-reassign
    element.style.overflow = 'visible';

    const height = element.scrollHeight;

    // eslint-disable-next-line no-param-reassign
    element.style.height = originalHeight;
    // eslint-disable-next-line no-param-reassign
    element.style.overflow = originalOverflow;

    if (wasHidden) {
      element.setAttribute('hidden', true);
    }

    element.setAttribute('data-open-size', height);

    return height;
  }

  static setParentActiveState(parentRow, isActive) {
    if (!parentRow) {
      return;
    }

    parentRow.classList.toggle('brand-venues__destination-row--parent--active', Boolean(isActive));
  }

  static syncParentHighlightStates(listElement) {
    if (!listElement) {
      return;
    }

    const parentRows = listElement.querySelectorAll('.brand-venues__destination-row--parent');

    parentRows.forEach((parentRow) => {
      const parentCheckbox = parentRow.querySelector('input[type="checkbox"]');
      const childContainer = parentRow.nextElementSibling;
      const hasCheckedChildren = childContainer ? childContainer.querySelector('.brand-venues__destination-row--child input[type="checkbox"]:checked') : null;

      const shouldHighlight = !!parentCheckbox?.checked || Boolean(hasCheckedChildren);
      FormElements.setParentActiveState(parentRow, shouldHighlight);
    });
  }
}

export default FormElements;
